import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/system';
const useMouse = (ref) => {
  const [mouse, setMouse] = useState({ elX: -1, elY: -1, elW: 0, elH: 0 });
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const handleMouseMove = (e) => {
      const rect = node.getBoundingClientRect();
      const elX = e.clientX - rect.left;
      const elY = e.clientY - rect.top;
      setMouse({ elX, elY, elW: rect.width, elH: rect.height });
    };

    const handleMouseLeave = () => {
      setMouse({ elX: -1, elY: -1, elW: 0, elH: 0 });
    };

    node.addEventListener('mousemove', handleMouseMove);
    node.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      node.removeEventListener('mousemove', handleMouseMove);
      node.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [ref]);
  return mouse;
};

const hoverScale = 1.05;
const magnifierSize = 225;
const magnifierBorderWidth = 2;

const showAnim = keyframes`
  0% { transform: scale3d(0, 0, 0); opacity: 0; }
  50% { transform: scale3d(1.1, 1.1, 1); }
  100% { transform: scale3d(1, 1, 1); opacity: 1; }
`;

const hideAnim = keyframes`
  0% { transform: scale3d(1, 1, 1); opacity: 1; }
  50% { transform: scale3d(1.1, 1.1, 1); }
  100% { transform: scale3d(0, 0, 0); opacity: 0; }
`;

const MagnifierWrap = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'shown',
})(({ theme, shown }) => ({
  position: 'absolute',
  border: `${magnifierBorderWidth}px solid ${theme.palette.primary.main}`,
  borderRadius: '50%',
  cursor: 'none',
  width: magnifierSize,
  height: magnifierSize,
  pointerEvents: 'none',
  zIndex: 9999,
  backgroundColor: '#fff',
  animation:
    shown === null ? 'none' : shown ? `${showAnim} 300ms ease-in-out both` : `${hideAnim} 300ms ease-in-out both`,
}));

const Magnifier = ({ zoom, src, imageRef, imageWidth, imageHeight }) => {
  const { elX, elY, elW, elH } = useMouse(imageRef);
  const [shown, setShown] = useState(null);

  // Remove disabled from show logic
  const show = elX >= 0 && elX <= elW && elY >= 0 && elY <= elH;

  useEffect(() => {
    if (shown !== show) setShown(show);
  }, [show]);

  if (!show) return null;

  const imageSize = Math.max(elW, elH);
  const widthRatio = imageSize / elW;
  const heightRatio = imageSize / elH;

  const xScaling = (hoverScale - 1) * elW * 2;
  const yScaling = (hoverScale - 1) * elH * 2;

  const xImageOffset = Math.max(widthRatio * (elX * zoom + xScaling - magnifierBorderWidth - magnifierSize / 2), 0);
  const yImageOffset = Math.max(heightRatio * (elY * zoom + yScaling - magnifierBorderWidth - magnifierSize / 2), 0);

  const xElOffset = elX - magnifierSize / 2 - 10;
  const yElOffset = elY - magnifierSize / 2 - 10;

  return (
    <MagnifierWrap
      shown={shown}
      sx={{
        left: `${xElOffset}px`,
        top: `${yElOffset}px`,
        background: `url("${src}")`,
        backgroundSize: `${imageSize * zoom}px ${imageSize * zoom}px`,
        backgroundPosition: `-${xImageOffset}px -${yImageOffset}px`,
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
};

export default Magnifier;
